import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Building2, Car, ArrowLeft, Menu, X } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { authService } from '../services/auth';
import { useAuth } from '../contexts/AuthContext';
import LanguageToggle from '../components/LanguageToggle';

const LoginSelection: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { refreshUser } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    // Only clear auth state if user is actually visiting this page intentionally
    // Don't clear if they have valid auth data (might be a redirect)
    const hasValidAuth = authService.isAuthenticated() && authService.getCurrentUser();
    
    if (!hasValidAuth) {
      console.log('LoginSelection: Clearing auth data (no valid auth found)');
      authService.clearAuthData();
      refreshUser();
    } else {
      console.log('LoginSelection: User has valid auth, redirecting to dashboard');
      navigate('/dashboard', { replace: true });
    }
  }, [refreshUser, navigate]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white">
      {/* Navigation */}
      <nav className="bg-white shadow-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <Link to="/">
                <h1 className="text-2xl font-bold" style={{ color: '#008B8B' }}>Rayyi7</h1>
              </Link>
            </div>

            <div className="flex items-center space-x-4">
              <div className="hidden md:flex items-center space-x-8">
              <Link to="/#features" className="text-gray-700 hover:text-[#008B8B] transition">
                {t('nav.features')}
              </Link>
              <Link to="/#about" className="text-gray-700 hover:text-[#008B8B] transition">
                {t('nav.about')}
              </Link>
              <Link to="/pricing" className="text-gray-700 hover:text-[#008B8B] transition">
                {t('nav.pricing')}
              </Link>
              </div>

              <LanguageToggle />

              <button
                className="md:hidden"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              >
              {mobileMenuOpen ? (
                <X className="h-6 w-6 text-gray-700" />
              ) : (
                <Menu className="h-6 w-6 text-gray-700" />
              )}
              </button>
            </div>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-gray-200">
            <div className="px-4 py-3 space-y-3">
              <Link 
                to="/#features" 
                className="block text-gray-700 hover:text-[#008B8B] transition py-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                {t('nav.features')}
              </Link>
              <Link 
                to="/#about" 
                className="block text-gray-700 hover:text-[#008B8B] transition py-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                {t('nav.about')}
              </Link>
              <Link 
                to="/pricing" 
                className="block text-gray-700 hover:text-[#008B8B] transition py-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                {t('nav.pricing')}
              </Link>
            </div>
          </div>
        )}
      </nav>

      <div className="flex items-center justify-center px-4 py-12 min-h-[calc(100vh-64px)]">
        <div className="max-w-4xl w-full">
          {/* Back to Home */}
          <button
            onClick={() => navigate('/')}
            className="mb-8 flex items-center text-gray-600 hover:text-[#008B8B] transition"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            {t('login.backToHome')}
          </button>

          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">{t('login.welcomeTo')}</h1>
            <p className="text-xl text-gray-600">{t('login.selectAccess')}</p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Garage Login */}
            <Link
              to="/login/garage"
              className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-all transform hover:-translate-y-1 border-2 border-transparent hover:border-[#008B8B]"
            >
              <div className="flex flex-col items-center text-center">
                <div className="bg-[#008B8B] bg-opacity-10 p-4 rounded-full mb-6">
                  <Building2 className="w-12 h-12" style={{ color: '#008B8B' }} />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-3">{t('login.garageLogin')}</h2>
                <p className="text-gray-600 mb-4">
                  {t('login.garageDesc')}
                </p>
                <div className="text-sm text-gray-500">
                  {t('login.garageMethod')}
                </div>
              </div>
            </Link>

            {/* Car Owner Login */}
            <Link
              to="/login/customer"
              className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-all transform hover:-translate-y-1 border-2 border-transparent hover:border-[#FF8C00]"
            >
              <div className="flex flex-col items-center text-center">
                <div className="bg-[#FF8C00] bg-opacity-10 p-4 rounded-full mb-6">
                  <Car className="w-12 h-12" style={{ color: '#FF8C00' }} />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-3">{t('login.carOwnerLogin')}</h2>
                <p className="text-gray-600 mb-4">
                  {t('login.carOwnerDesc')}
                </p>
                <div className="text-sm text-gray-500">
                  {t('login.carOwnerMethod')}
                </div>
              </div>
            </Link>
          </div>

          <div className="mt-12 text-center text-gray-600 bg-blue-50 p-6 rounded-lg">
            <p className="text-sm">
              {t('login.carOwnerNote')}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginSelection;